/**
 * The ONLY routes a restricted staff member (public.restricted_staff —
 * insider-threat containment, docs/plans/2026-08-10-restricted-staff-lockdown.md)
 * may reach: GridView and My Schedule. Mirrors
 * src/routes/locationAllowlist.ts exactly — same registry-pattern vs.
 * live-pathname split, same fail-closed-by-construction posture (a route
 * added after this file was written is never in the set, so it's blocked
 * for a restricted account by default). Single source of truth for both
 * route-guard enforcement (RouteGateGuard.tsx) and nav visibility
 * (Layout.tsx).
 *
 * The RLS boundary (supabase/migrations/20260810130000_restricted_staff_lockdown.sql)
 * is the real security control — this only controls what renders/is
 * discoverable client-side. Includes the legacy /childcare/grid and
 * /childcare/operations redirect paths (both redirectTo "/grid" in the
 * registry) so a restricted account bookmarking/typing either doesn't get
 * stuck: RestrictedRouteEnforcer matches live pathname, and a redirect
 * route resolves to /grid before RouteGateGuard would ever see it, but
 * listing them here keeps isRestrictedAllowedPathname() correct
 * independent of that resolution order.
 */
import { matchPath } from "react-router-dom";

export const RESTRICTED_STAFF_ALLOWED_PATHS: ReadonlySet<string> = new Set([
  "/grid",
  "/activities",
  "/my-schedule",
  "/childcare/grid",
  "/childcare/operations",
]);

/** Where a blocked restricted-staff navigation redirects to. Deliberately
 * NOT "/me" (RouteGateGuard's default denied-route target) — a restricted
 * user can't reach /me either, so that redirect would just bounce again. */
export const RESTRICTED_STAFF_HOME_PATH = "/my-schedule";

/** Registry-path-pattern match (route.path as declared in registry.tsx) —
 * used by RouteGateGuard, which already knows which registry entry it's
 * guarding. */
export function isRestrictedAllowedRoutePath(routePath: string): boolean {
  return RESTRICTED_STAFF_ALLOWED_PATHS.has(routePath);
}

/**
 * Live-URL match — used by the router-level enforcer
 * (RestrictedRouteEnforcer), which only has `location.pathname`, not a
 * registry entry. Same rationale as isLocationAllowedPathname: a
 * `kind:"redirect"` entry or the "*" catch-all bypasses RouteGateGuard
 * entirely. `end: true` so a future nested path doesn't accidentally match
 * a shorter allowed prefix.
 */
export function isRestrictedAllowedPathname(pathname: string): boolean {
  return [...RESTRICTED_STAFF_ALLOWED_PATHS].some(
    (pattern) => matchPath({ path: pattern, end: true }, pathname) !== null,
  );
}
